import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage quiz sound effects
 * Handles countdown beeps, option appearance sounds, and background music
 */
const useQuizSounds = ({
  isMuted = false,
  startInteraction
}) => {
  const audioContextRef = useRef(null);
  const backgroundAudioRef = useRef(null);

  // Initialize audio context
  useEffect(() => {
    if (!startInteraction) return;
    if (!isMuted) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    return () => {
      if (!audioContextRef.current) return;
      const ctx = audioContextRef.current;
      // Delay closing to allow final sounds to finish playing
      setTimeout(() => {
        if (ctx?.state !== 'closed') {
          ctx?.close().catch(() => { });
        }
        if (audioContextRef.current === ctx) {
          audioContextRef.current = null;
        }
      }, 2000);
    };
  }, [isMuted, startInteraction]);

  // Create beep sound using Web Audio API
  const createBeep = (frequency, duration, type = 'sine') => {
    if (isMuted || !audioContextRef.current) return;

    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
      oscillator.type = type;

      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);

      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);
    } catch (error) {
      console.error('Error creating beep:', error);
    }
  };

  // Play countdown beep sound
  const playCountdownSound = () => {
    createBeep(440, 0.2);
  };

  // Play final countdown sound (higher pitch)
  const playFinalCountdownSound = () => {
    createBeep(880, 0.3);
  };

  // Play option appear sound
  const playOptionAppearSound = () => {
    createBeep(330, 0.1);
  };

  // Play start sound (ascending notes)
  const playStartSound = () => {
    createBeep(523, 0.15);
    setTimeout(() => createBeep(659, 0.15), 150);
    setTimeout(() => createBeep(784, 0.3), 300);
  };

  // Play time up sound
  const playTimeUpSound = () => {
    createBeep(220, 0.5, 'sawtooth');
    setTimeout(() => createBeep(200, 0.5, 'sawtooth'), 200);
  };

  // Create and play background music
  const createBackgroundMusic = () => {
    if (isMuted) return;

    if (!backgroundAudioRef.current) {
      backgroundAudioRef.current = new Audio('https://s3-prod-streamalive.s3.amazonaws.com/Sounds/quiz-bg.mp3');
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.volume = 0.3;

      backgroundAudioRef.current.addEventListener('error', () => {
        console.log('MP3 not found, continuing without background music');
        backgroundAudioRef.current = null;
      });
    }

    backgroundAudioRef.current?.play().catch((error) => {
      console.log('MP3 failed to play, continuing without background music', error);
      backgroundAudioRef.current = null;
    });
  };

  const stopBackgroundMusic = () => {
    console.log('stopBackgroundMusic');
    if (backgroundAudioRef.current) {
      console.log('attempting to stop background music');
      backgroundAudioRef.current.pause();
      backgroundAudioRef.current.currentTime = 0;
    }
  };

  const pauseBackgroundMusic = () => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
    }
  };

  const resumeBackgroundMusic = () => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.play();
    } else {
      createBackgroundMusic();
    }
  };

  useEffect(() => {
    if (isMuted && backgroundAudioRef.current) {
      stopBackgroundMusic();
    }
  }, [isMuted]);

  return {
    playCountdownSound,
    playFinalCountdownSound,
    playOptionAppearSound,
    playStartSound,
    playTimeUpSound,
    createBackgroundMusic,
    stopBackgroundMusic,
    pauseBackgroundMusic,
    resumeBackgroundMusic
  };
};

export default useQuizSounds;
